
import { Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface PromptDisplayProps {
  value: string;
  isReadOnly: boolean;
}

export const PromptDisplay = ({ value, isReadOnly }: PromptDisplayProps) => {
  // Process prompt to highlight sections
  const processPrompt = (prompt: string) => {
    if (!prompt) return [];
    
    // Split by section headers (### Title)
    const sectionRegex = /### ([^\n]+)/g;
    const sections = prompt.split(sectionRegex).filter(Boolean);
    
    const processed = [];
    
    // First item might not have a header
    if (!prompt.trimStart().startsWith('###')) {
      processed.push({
        title: 'Preamble',
        content: sections[0]
      });
      sections.shift();
    }
    
    // Process remaining sections
    for (let i = 0; i < sections.length; i += 2) {
      if (i + 1 < sections.length) {
        processed.push({
          title: sections[i],
          content: sections[i + 1]
        });
      }
    }
    
    return processed;
  };
  
  const promptSections = processPrompt(value);
  
  return (
    <Card className="relative bg-muted/50 border-muted">
      <ScrollArea className="h-[400px] w-full rounded-md">
        <div className="p-4 font-mono text-sm relative">
          {promptSections.length > 0 ? (
            promptSections.map((section, index) => (
              <div key={index} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="font-mono text-xs bg-primary/5">
                    {section.title}
                  </Badge>
                </div>
                <div className="whitespace-pre-wrap pl-2 border-l-2 border-primary/20">
                  {section.content}
                </div>
                {index < promptSections.length - 1 && (
                  <Separator className="my-4 opacity-30" />
                )}
              </div>
            ))
          ) : (
            <div className="text-muted-foreground italic py-4 text-center">
              {isReadOnly 
                ? "No prompt content available for this agent." 
                : "No prompt content defined yet. Use the section cards below to start creating a prompt."}
            </div>
          )}
        </div>
      </ScrollArea>
      {isReadOnly && (
        <div className="absolute top-2 right-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
    </Card>
  );
};
